import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useOnlineStore } from '../../store/online-store';
import {
  acceptRematchInvite as socketAcceptRematchInvite,
  declineRematchInvite as socketDeclineRematchInvite,
} from '../../services/socket';
import { trackRematchAccepted, trackRematchDeclined } from '../../utils/analytics';
import { variantShortLabel } from '../../utils/variant-labels';
import { playSound } from '../../utils/audio';
import { vibrate } from '../../utils/vibration';
import { log } from '../../utils/logger';
import './GlobalIncomingRematchBanner.css';

/**
 * Global incoming rematch-invite banner.
 *
 * When an opponent still on their game-over screen requests a rematch and this
 * client has ALREADY left the game, the server pushes a `rematch:invite` to the
 * user's current socket. This banner surfaces it on EVERY screen (mounted via
 * App's `challengeOverlays` slot) — the exact same treatment as an incoming
 * direct challenge — so the rematch is impossible to miss. Accepting takes the
 * user straight back into the new game (navigation is driven by the multiplayer
 * status flipping to 'playing' on `rematch:start`). Auto-gates on
 * `incomingRematch`.
 */
export default function GlobalIncomingRematchBanner() {
  const { t } = useTranslation();
  const invite = useOnlineStore((s) => s.incomingRematch);
  const setIncomingRematch = useOnlineStore((s) => s.setIncomingRematch);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const roomId = invite?.roomId;
  const expiresAt = invite?.expiresAt;

  // Countdown + auto-decline on expiry. The server also expires the invite
  // (rematch:invite-expired), but the client counts down independently so the
  // number stays live even if that event is delayed.
  useEffect(() => {
    if (!roomId || expiresAt == null) return;
    log.info('IncomingRematchBanner', 'Rematch invite armed', {
      roomId,
      secondsToExpiry: Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)),
    });
    let timer = 0;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) {
        window.clearInterval(timer);
        log.info('IncomingRematchBanner', 'Rematch invite expired, auto-declining', { roomId });
        trackRematchDeclined();
        socketDeclineRematchInvite(roomId).catch((e) =>
          log.error('IncomingRematchBanner', 'Auto-decline emit failed', e)
        );
        setIncomingRematch(null);
      }
    };
    timer = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(timer);
  }, [roomId, expiresAt, setIncomingRematch]);

  // Audio + haptic alert — fires once per incoming invite (keyed on roomId).
  // Stesso suono della sfida in arrivo, ed e' la stessa notizia: un avversario
  // chiede di giocare e aspetta. Non quello dell'inizio partita.
  useEffect(() => {
    if (!roomId) return;
    playSound('richiesta');
    vibrate([120, 60, 120]);
  }, [roomId]);

  // Browser-tab title badge — visible when the tab is in the background.
  useEffect(() => {
    if (!roomId) return;
    const original = document.title;
    document.title = `(1) ${t('online.rematch.invite.tabBadge')} · ${original}`;
    return () => { document.title = original; };
  }, [roomId, t]);

  if (!invite) return null;

  const variantLabel = (variant: string): string => variantShortLabel(t, variant);

  const timeControlLabel = (tc: string): string => {
    if (tc === 'none') return t('game.timeControls.noTimeLimit');
    const parts = tc.split('-');
    if (parts.length === 2 && parts[0] && parts[1]) {
      const category = parts[0];
      return `${parts[1]} (${category.charAt(0).toUpperCase() + category.slice(1)})`;
    }
    return tc;
  };

  const handleAccept = async () => {
    log.info('IncomingRematchBanner', 'Accept clicked', { from: invite.fromUsername });
    setLoading(true);
    trackRematchAccepted();
    try {
      await socketAcceptRematchInvite(invite.roomId);
      // Navigation happens via the multiplayer status flipping to 'playing'
      // on the ensuing rematch:start (App's effect switches to the game screen).
      setIncomingRematch(null);
    } catch (e) {
      log.error('IncomingRematchBanner', 'Accept failed', e);
      // The offer is gone (expired/opponent left) — clear the banner.
      setIncomingRematch(null);
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    log.info('IncomingRematchBanner', 'Decline clicked', { from: invite.fromUsername });
    trackRematchDeclined();
    try {
      await socketDeclineRematchInvite(invite.roomId);
    } catch (e) { log.error('IncomingRematchBanner', 'Decline emit failed', e); }
    setIncomingRematch(null);
  };

  return (
    <div
      className="incoming-rematch-banner"
      role="alert"
      aria-live="assertive"
      aria-label={t('online.rematch.invite.incoming')}
    >
      <div className="irb-card">
        <span className="irb-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6" />
            <path d="M3 8a9 9 0 1 1-.5 4.5" />
          </svg>
        </span>

        <div className="irb-text">
          {/* Chi non ha punteggio (ospite o bot) va nominato senza numero:
              la variante «NoRating» è la stessa frase senza « (rating) ». */}
          <Trans
            i18nKey={invite.fromRating == null
              ? 'online.rematch.invite.messageNoRating'
              : 'online.rematch.invite.message'}
            values={{
              name: invite.fromUsername,
              rating: invite.fromRating,
              timeControl: timeControlLabel(String(invite.timeControl)),
              variant: variantLabel(String(invite.variant)),
            }}
            components={{ b: <b /> }}
          />
        </div>

        <span className="irb-timer" aria-hidden="true">{timeLeft}s</span>

        <div className="irb-actions">
          <button type="button" className="irb-btn irb-decline" onClick={handleDecline} disabled={loading}>
            {t('online.rematch.decline')}
          </button>
          <button type="button" className="irb-btn irb-accept" onClick={handleAccept} disabled={loading}>
            {loading ? <span className="irb-spinner" /> : t('online.rematch.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
